/**
 * The one place the app talks to the server.
 *
 * Everything goes through `request`, so token attachment, 401 handling, error
 * shaping and the money convention are decided once rather than in forty
 * components. That also keeps the door open on MOBILE-1: when the field app
 * needs a local store in front of the network, it replaces this module's guts
 * and nothing above it changes.
 */

import { device } from "./device";
import type {
  AssetOut,
  QuoteDeliveryOut,
  RecipientOut,
  OverviewOut,
  CertificateOut,
  FormSubmissionOut,
  FormTemplateOut,
  SubmissionIn,
  TestDueRow,
  CatalogueItemOut,
  CommissionRequest,
  CommissionResult,
  ContractDetailOut,
  ContractOut,
  Frequency,
  ContractVisitOut,
  DispatchAssign,
  DispatchAssignOut,
  DispatchDay,
  AccountingStatus,
  BillingDueRow,
  ContactIn,
  CreditRequest,
  CustomerDetail,
  CustomerIn,
  CustomerRow,
  ImportResult,
  ItemIn,
  KitDetailOut,
  KitIn,
  SupplierOut,
  SiteIn,
  PaymentIn,
  RaisedInvoice,
  DocumentKind,
  ExportRow,
  FieldRef,
  InvoiceDetail,
  InvoiceDraftOut,
  InvoiceRow,
  InvoiceSendResult,
  OrgOut,
  ChannelsOut,
  ChannelsPatch,
  TestMailOut,
  PushOut,
  TemplateOut,
  InvoiceOut,
  JobDetailOut,
  ReturnReason,
  ToBookOut,
  ToRaise,
  JobOut,
  KitOut,
  KitPreviewOut,
  PublicAcceptResult,
  PublicQuote,
  PullResponse,
  PushResponse,
  QuoteOut,
  RenewalPreview,
  RepeatFaultOut,
  SendQuoteResult,
  ShortfallOut,
  SiteOut,
  StockLevelOut,
  StockLocationOut,
  StockMovementOut,
  StockMovementResult,
  TokenResponse,
  UserOut,
  PersonOut,
  PersonWithPassword,
  Role,
  SetupStatus,
  VariationOut,
  VariationResult,
  WorkingSetResponse,
} from "./types";

/** Relative in every environment: Vite proxies /v1 in dev, nginx does in prod. */
const BASE = "/v1";

const TOKEN_KEY = "keystone.token";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** A 401 means the session is gone; anything else is a problem with the request. */
  get isAuthFailure() {
    return this.status === 401;
  }

  /** 409s are the server refusing on business grounds — a guard, not a bug.
   *  They carry a message written for a person, so show it as-is. */
  get isBusinessRule() {
    return this.status === 409;
  }
}

/* ------------------------------------------------------------------ token */

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private browsing, blocked site data. Losing the token means logging in
    // again, not a broken app.
    return null;
  }
}

export function writeToken(token: string | null) {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY);
    else localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore — the session then lasts until reload */
  }
}

/** Set by the auth provider so a 401 anywhere can end the session once,
 *  instead of every caller having to know what to do about it. */
let onAuthFailure: (() => void) | null = null;
export function setAuthFailureHandler(fn: (() => void) | null) {
  onAuthFailure = fn;
}

/* ---------------------------------------------------------------- request */

type Options = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Login is the one call that must not trigger the session-expiry handler. */
  anonymous?: boolean;
  /**
   * Send the token if there happens to be one, and never end the session over
   * the answer. For the customer's quote page, which is the same URL whether a
   * stranger or the office opens it: the stranger has no token and gets the
   * page, the office's token identifies them as proofreading so their look
   * does not count as the customer opening it.
   */
  opportunistic?: boolean;
};

async function request<T>(path: string, options: Options = {}): Promise<T> {
  const { method = "GET", body, signal, anonymous = false, opportunistic = false } = options;

  const headers: Record<string, string> = { Accept: "application/json" };
  // A file upload sets its own multipart boundary; anything else is JSON.
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  if (body !== undefined && !isForm) headers["Content-Type"] = "application/json";

  const token = readToken();
  if (token && !anonymous) headers["Authorization"] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    // fetch only rejects on a transport failure, which for a field app is the
    // normal condition rather than an exception. Say so plainly.
    if (signal?.aborted) throw cause;
    throw new ApiError(0, "Can't reach the server. Check your connection.", cause);
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const payload = isJson ? await response.json().catch(() => null) : await response.text();

  if (!response.ok) {
    if (response.status === 401 && !anonymous && !opportunistic) onAuthFailure?.();
    throw new ApiError(response.status, messageFrom(payload, response.status), payload);
  }

  return payload as T;
}

/**
 * Open a document the API serves behind a token — a quote or invoice PDF.
 *
 * A plain `<a href="/v1/…/pdf">` cannot carry the bearer token, so it gets a
 * 401 and the office sees a line of JSON where a PDF should be. This fetches
 * with the token and opens the result. The window is opened first, while the
 * click is still the thing happening, because a popup opened after an
 * `await` is blocked by every browser that blocks popups.
 */
export async function openDocument(path: string): Promise<void> {
  const tab = window.open("", "_blank");
  const token = readToken();
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (cause) {
    tab?.close();
    throw new ApiError(0, "Can't reach the server. Check your connection.", cause);
  }
  if (!response.ok) {
    tab?.close();
    if (response.status === 401) onAuthFailure?.();
    throw new ApiError(response.status, `Couldn't open the document (${response.status}).`);
  }
  const url = URL.createObjectURL(await response.blob());
  if (tab) tab.location.href = url;
  else window.location.href = url;
  // Long enough for the tab to load it; object URLs otherwise live until the
  // page that made them is closed.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function messageFrom(payload: unknown, status: number): string {
  if (typeof payload === "string" && payload.trim()) return payload;
  if (payload && typeof payload === "object") {
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
    // FastAPI validation errors arrive as a list of {loc, msg}.
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string; loc?: unknown[] };
      const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : undefined;
      return field ? `${String(field)}: ${first.msg ?? "is invalid"}` : (first.msg ?? "That request was rejected.");
    }
  }
  if (status === 401) return "Your session has expired. Sign in again.";
  if (status === 403) return "You don't have access to that.";
  if (status === 404) return "Not found.";
  if (status >= 500) return "The server had a problem. Try again in a moment.";
  return "That request was rejected.";
}

/* ------------------------------------------------------------------- api */

export const api = {
  auth: {
    login: (email: string, password: string, deviceId?: string) =>
      request<TokenResponse>("/auth/login", {
        method: "POST",
        // The device id rides along so the token carries it: it is the
        // tiebreak that makes field-level merge converge identically on every
        // replica (D3), and the server cannot invent one.
        body: { email, password, device_id: deviceId },
        anonymous: true,
      }),
    me: () => request<UserOut>("/auth/me"),
    changePassword: (current_password: string, new_password: string) =>
      request<TokenResponse>("/auth/password", { method: "POST", body: { current_password, new_password } }),
  },

  setup: {
    status: () => request<SetupStatus>("/setup", { anonymous: true }),
    create: (body: { business_name: string; full_name: string; email: string; password: string; setup_token?: string }) =>
      request<TokenResponse>("/setup", { method: "POST", body, anonymous: true }),
  },

  people: {
    list: () => request<PersonOut[]>("/users"),
    roles: () => request<{ role: Role; means: string }[]>("/roles"),
    add: (body: { full_name: string; email: string; role: Role; phone?: string; is_field?: boolean }) =>
      request<PersonWithPassword>("/users", { method: "POST", body }),
    update: (id: string, body: Partial<Omit<PersonOut, "id" | "is_you" | "must_change_password">>) =>
      request<PersonOut>(`/users/${id}`, { method: "PATCH", body }),
    resetPassword: (id: string) => request<PersonWithPassword>(`/users/${id}/reset-password`, { method: "POST" }),
  },

  jobs: {
    list: (
      params: { status?: string; assigned_to?: string; day?: string; quote_id?: string; customer_id?: string } = {},
    ) =>
      request<JobOut[]>(`/jobs${query(params)}`),
    get: (id: string) => request<JobDetailOut>(`/jobs/${id}`),
    create: (body: {
      customer_id: string;
      site_id: string;
      type: string;
      title: string;
      description?: string;
      purchase_order_number?: string;
      quoted_hours?: number;
      reported_at?: string;
      billing_mode?: string;
      travel_km_one_way?: number | null;
    }) => request<JobDetailOut>("/jobs", { method: "POST", body }),
    arrive: (id: string) => request<JobDetailOut>(`/jobs/${id}/arrive`, { method: "POST" }),
    complete: (id: string) => request<JobDetailOut>(`/jobs/${id}/complete`, { method: "POST" }),
    takeBreak: (id: string) => request<JobDetailOut>(`/jobs/${id}/break`, { method: "POST" }),
    resume: (id: string) => request<JobDetailOut>(`/jobs/${id}/resume`, { method: "POST" }),
    leave: (id: string, body: { reason: ReturnReason; note?: string | null; hours?: number | null }) =>
      request<JobDetailOut>(`/jobs/${id}/leave`, { method: "POST", body }),
    toBook: () => request<ToBookOut[]>("/jobs/to-book"),
    addLine: (id: string, line: Record<string, unknown>) =>
      request<JobDetailOut>(`/jobs/${id}/lines`, { method: "POST", body: line }),
    correctLine: (id: string, lineId: string, patch: Record<string, unknown>) =>
      request<JobDetailOut>(`/jobs/${id}/lines/${lineId}`, { method: "PATCH", body: patch }),
    removeLine: (id: string, lineId: string) =>
      request<JobDetailOut>(`/jobs/${id}/lines/${lineId}`, { method: "DELETE" }),
    moveLine: (id: string, lineId: string, toJobId: string) =>
      request<JobDetailOut>(`/jobs/${id}/lines/${lineId}/move`, { method: "POST", body: { to_job_id: toJobId } }),
    cancel: (id: string, reason: string) =>
      request<JobDetailOut>(`/jobs/${id}/cancel`, { method: "POST", body: { reason } }),
    remove: (id: string) => request<void>(`/jobs/${id}`, { method: "DELETE" }),
    bookTime: (id: string, entry: Record<string, unknown>) =>
      request<JobDetailOut>(`/jobs/${id}/time`, { method: "POST", body: entry }),
    acceptOverrun: (id: string, payload: { hours: number; reason?: string }) =>
      request<JobDetailOut>(`/jobs/${id}/accept-overrun`, { method: "POST", body: payload }),
    dispatch: (day: string, days = 1) => request<DispatchDay>(`/dispatch${query({ day, days })}`),
    assign: (body: DispatchAssign) => request<DispatchAssignOut>("/dispatch/assign", { method: "POST", body }),
    writeUp: (
      id: string,
      body: {
        work_done?: string;
        purchase_order_number?: string;
        description?: string;
        billing_mode?: string;
        travel_km_one_way?: number | null;
        adjustment_bp?: number | null;
        adjustment_cents?: number | null;
        adjustment_label?: string | null;
      },
    ) =>
      request<JobDetailOut>(`/jobs/${id}`, { method: "PATCH", body }),
  },

  quotes: {
    get: (id: string) => request<QuoteOut>(`/quotes/${id}`),
    /** Who this quote can go to, primary contact first (QUOTE-10). */
    recipients: (id: string) => request<RecipientOut[]>(`/quotes/${id}/recipients`),
    /** Every attempt to send it, newest first. */
    deliveries: (id: string) => request<QuoteDeliveryOut[]>(`/quotes/${id}/deliveries`),
    list: () => request<QuoteOut[]>("/quotes"),
    create: (payload: Record<string, unknown>) =>
      request<QuoteOut>("/quotes", { method: "POST", body: payload }),
    reprice: (id: string, targetMarginBp: number) =>
      request<QuoteOut>(`/quotes/${id}/reprice`, {
        method: "POST",
        body: { target_margin_bp: targetMarginBp },
      }),
    send: (id: string, payload?: { to?: string; note?: string }) =>
      request<SendQuoteResult>(`/quotes/${id}/send`, { method: "POST", body: payload }),
    rotateLink: (id: string) => request<SendQuoteResult>(`/quotes/${id}/rotate-link`, { method: "POST" }),
    // QUOTE-12: the editor. Every edit answers with the whole quote, repriced.
    update: (
      id: string,
      body: {
        title?: string;
        deposit_pct_bp?: number;
        valid_until?: string | null;
        pricing?: "price_book" | "target_margin";
        adjustment_bp?: number | null;
        adjustment_cents?: number | null;
        adjustment_label?: string | null;
      },
    ) =>
      request<QuoteOut>(`/quotes/${id}`, { method: "PATCH", body }),
    addSection: (
      id: string,
      body: { kit_id?: string; title?: string; driver_qty?: number; is_optional?: boolean },
    ) => request<QuoteOut>(`/quotes/${id}/sections`, { method: "POST", body }),
    updateSection: (id: string, sectionId: string, body: { title?: string; is_optional?: boolean }) =>
      request<QuoteOut>(`/quotes/${id}/sections/${sectionId}`, { method: "PATCH", body }),
    removeSection: (id: string, sectionId: string) =>
      request<QuoteOut>(`/quotes/${id}/sections/${sectionId}`, { method: "DELETE" }),
    addLine: (
      id: string,
      sectionId: string,
      body: { catalogue_item_id?: string; description?: string; kind?: string; qty?: number; uom?: string; unit_cost_cents?: number },
    ) => request<QuoteOut>(`/quotes/${id}/sections/${sectionId}/lines`, { method: "POST", body }),
    updateLine: (
      id: string,
      lineId: string,
      body: { description?: string; qty?: number; unit_cost_cents?: number; unit_sell_cents?: number | null },
    ) =>
      request<QuoteOut>(`/quotes/${id}/lines/${lineId}`, { method: "PATCH", body }),
    removeLine: (id: string, lineId: string) => request<QuoteOut>(`/quotes/${id}/lines/${lineId}`, { method: "DELETE" }),
    accept: (id: string, payload: Record<string, unknown> = {}) =>
      request<{
        quote_id: string;
        job_ids: string[];
        asset_ids: string[];
        contract_id: string | null;
        purchase_order_ids: string[];
        deposit_invoice_id: string | null;
      }>(`/quotes/${id}/accept`, { method: "POST", body: payload }),
  },

  /**
   * The customer's link. Anonymous on purpose, and anonymous is load-bearing:
   * if the office happens to be signed in on the same browser — which is
   * exactly what happens when they open the link to check it looks right —
   * attaching their token would make the page a different request from the one
   * the customer makes, and the version they proofread would not be the
   * version that was sent.
   */
  publicQuotes: {
    get: (token: string) =>
      request<PublicQuote>(`/public/quotes/${encodeURIComponent(token)}`, { opportunistic: true }),
    accept: (token: string, payload: { accepted_by_name: string; signature_url?: string | null }) =>
      request<PublicAcceptResult>(`/public/quotes/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        body: payload,
        anonymous: true,
      }),
  },

  contracts: {
    list: (params: { needs_attention?: boolean; include_ended?: boolean } = {}) =>
      request<ContractOut[]>(`/contracts${query(params as Record<string, string | number>)}`),
    get: (id: string) => request<ContractDetailOut>(`/contracts/${id}`),
    due: (withinDays = 30) => request<ContractVisitOut[]>(`/contracts/visits/due${query({ within_days: withinDays })}`),
    book: (visitId: string, payload: { scheduled_start?: string; assigned_user_id?: string; title?: string } = {}) =>
      request<ContractVisitOut>(`/contracts/visits/${visitId}/book`, { method: "POST", body: payload }),
    complete: (visitId: string) =>
      request<ContractVisitOut>(`/contracts/visits/${visitId}/complete`, { method: "POST" }),
    previewRenewal: (id: string, params: { term_months?: number; cpi_uplift_bp?: number } = {}) =>
      request<RenewalPreview>(`/contracts/${id}/renewal${query(params as Record<string, string | number>)}`),
    renew: (id: string, payload: { term_months?: number; cpi_uplift_bp?: number } = {}) =>
      request<ContractDetailOut>(`/contracts/${id}/renew`, { method: "POST", body: payload }),
    setBilling: (id: string, body: { bill_from: string | null; billing_frequency?: Frequency }) =>
      request<ContractOut>(`/contracts/${id}/billing`, { method: "PUT", body }),
    billingDue: () => request<BillingDueRow[]>("/contracts/billing/due"),
    raise: (id: string) => request<RaisedInvoice[]>(`/contracts/${id}/invoices`, { method: "POST" }),
    raiseAll: () => request<RaisedInvoice[]>("/contracts/billing/raise", { method: "POST" }),
  },

  stock: {
    locations: () => request<StockLocationOut[]>("/stock/locations"),
    levels: (params: { location_id?: string; q?: string; include_empty?: boolean } = {}) =>
      request<StockLevelOut[]>(`/stock/levels${query(params as Record<string, string | number>)}`),
    movements: (params: { location_id?: string; catalogue_item_id?: string; job_id?: string } = {}) =>
      request<StockMovementOut[]>(`/stock/movements${query(params)}`),
    shortfalls: () => request<ShortfallOut[]>("/stock/shortfalls"),
    transfer: (payload: {
      catalogue_item_id: string;
      qty: number;
      from_location_id: string;
      to_location_id: string;
    }) => request<StockMovementResult>("/stock/transfers", { method: "POST", body: payload }),
    count: (lines: { catalogue_item_id: string; location_id: string; counted: number }[]) =>
      request<StockMovementResult[]>("/stock/counts", { method: "POST", body: lines }),
    myVan: () => request<StockLocationOut | null>("/stock/my-van"),
  },

  variations: {
    list: (jobId: string) => request<VariationOut[]>(`/jobs/${jobId}/variations`),
    raise: (jobId: string, payload: Record<string, unknown>) =>
      request<VariationResult>(`/jobs/${jobId}/variations`, { method: "POST", body: payload }),
    approve: (id: string, note?: string) =>
      request<VariationResult>(`/variations/${id}/approve`, { method: "POST", body: { note } }),
    decline: (id: string, note?: string) =>
      request<VariationResult>(`/variations/${id}/decline`, { method: "POST", body: { note } }),
    withdraw: (id: string, note?: string) =>
      request<VariationResult>(`/variations/${id}/withdraw`, { method: "POST", body: { note } }),
  },

  invoices: {
    toRaise: () => request<ToRaise[]>("/invoices/to-raise"),
    list: (show: "all" | "unsent" | "owing" | "overdue" | "accounts" = "all") =>
      request<InvoiceRow[]>(`/invoices${query({ show })}`),
    get: (id: string) => request<InvoiceDetail>(`/invoices/${id}`),
    send: (id: string, body: { to: string; note?: string }) =>
      request<InvoiceSendResult>(`/invoices/${id}/send`, { method: "POST", body }),
    markSent: (id: string, body: { how: "by_hand" | "portal" | "other"; note?: string }) =>
      request<InvoiceDetail>(`/invoices/${id}/mark-sent`, { method: "POST", body }),
    preview: (jobId: string) => request<InvoiceDraftOut>(`/invoices/preview/${jobId}`),
    remove: (id: string) => request<void>(`/invoices/${id}`, { method: "DELETE" }),
    pay: (id: string, body: PaymentIn) =>
      request<InvoiceDetail>(`/invoices/${id}/payments`, { method: "POST", body }),
    removePayment: (id: string, paymentId: string) =>
      request<InvoiceDetail>(`/invoices/${id}/payments/${paymentId}`, { method: "DELETE" }),
    credit: (id: string, body: CreditRequest) =>
      request<InvoiceDetail>(`/invoices/${id}/credit`, { method: "POST", body }),
    fromJob: (jobId: string, payload: Record<string, unknown>) =>
      request<InvoiceOut>(`/invoices/from-job/${jobId}`, { method: "POST", body: payload }),
  },

  org: {
    get: () => request<OrgOut>("/org"),
    update: (body: Partial<OrgOut>) => request<OrgOut>("/org", { method: "PATCH", body }),
    uploadLogo: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return request<OrgOut>("/org/logo", { method: "PUT", body: form });
    },
    removeLogo: () => request<OrgOut>("/org/logo", { method: "DELETE" }),
    channels: () => request<ChannelsOut>("/org/channels"),
    saveChannels: (body: ChannelsPatch) => request<ChannelsOut>("/org/channels", { method: "PATCH", body }),
    testMail: () => request<TestMailOut>("/org/channels/test-mail", { method: "POST" }),
  },

  templates: {
    list: () => request<TemplateOut[]>("/templates"),
    fields: () => request<FieldRef[]>("/templates/fields"),
    upload: (kind: DocumentKind, file: File) => {
      const form = new FormData();
      form.append("kind", kind);
      form.append("file", file);
      return request<TemplateOut>("/templates", { method: "POST", body: form });
    },
    activate: (id: string) => request<TemplateOut>(`/templates/${id}/activate`, { method: "POST" }),
    deactivate: (id: string) => request<TemplateOut>(`/templates/${id}/deactivate`, { method: "POST" }),
    remove: (id: string) => request<void>(`/templates/${id}`, { method: "DELETE" }),
  },

  accounting: {
    status: () => request<AccountingStatus>("/accounting/status"),
    connect: () => request<{ authorize_url: string }>("/accounting/xero/connect", { method: "POST" }),
    disconnect: () => request<{ disconnected: boolean }>("/accounting/xero/disconnect", { method: "POST" }),
    push: () => request<PushOut>("/accounting/push", { method: "POST" }),
    exports: (needsAttention = false) =>
      request<ExportRow[]>(`/accounting/exports${query({ needs_attention: needsAttention ? "true" : undefined })}`),
    retry: (id: string) => request<ExportRow>(`/accounting/exports/${id}/retry`, { method: "POST" }),
  },

  customers: {
    list: (q?: string) => request<CustomerRow[]>(`/customers${query({ q })}`),
    get: (id: string) => request<CustomerDetail>(`/customers/${id}`),
    create: (body: CustomerIn) => request<CustomerDetail>("/customers", { method: "POST", body }),
    update: (id: string, body: Partial<Omit<CustomerIn, "site" | "contact">> & { on_credit_hold?: boolean }) =>
      request<CustomerDetail>(`/customers/${id}`, { method: "PATCH", body }),
    addSite: (id: string, body: SiteIn) =>
      request<CustomerDetail>(`/customers/${id}/sites`, { method: "POST", body }),
    updateSite: (siteId: string, body: SiteIn) =>
      request<CustomerDetail>(`/sites/${siteId}`, { method: "PATCH", body }),
    addContact: (id: string, body: ContactIn) =>
      request<CustomerDetail>(`/customers/${id}/contacts`, { method: "POST", body }),
    updateContact: (contactId: string, body: ContactIn) =>
      request<CustomerDetail>(`/contacts/${contactId}`, { method: "PATCH", body }),
    removeContact: (contactId: string) => request<CustomerDetail>(`/contacts/${contactId}`, { method: "DELETE" }),
    importCsv: (file: File, dryRun: boolean) => {
      const form = new FormData();
      form.append("file", file);
      return request<ImportResult>(`/customers/import${query({ dry_run: String(dryRun) })}`, {
        method: "POST",
        body: form,
      });
    },
  },

  sites: {
    list: () => request<SiteOut[]>("/sites"),
    get: (id: string) => request<SiteOut>(`/sites/${id}`),
    /** Type the one-way km in, or leave it out to have it worked out (D86). */
    distance: (id: string, km?: number) =>
      request<{ site: SiteOut; note: string | null }>(`/sites/${id}/distance`, {
        method: "POST",
        body: km === undefined ? {} : { km },
      }),
  },

  assets: {
    list: (params: { site_id?: string; serial?: string } = {}) =>
      request<AssetOut[]>(`/assets${query(params)}`),
    history: (id: string) => request<unknown[]>(`/assets/${id}/history`),
    repeatFaults: () => request<RepeatFaultOut[]>("/assets/repeat-faults"),
    /** The shells acceptance created that nobody has scanned yet. */
    awaitingInstall: (params: { job_id?: string; site_id?: string } = {}) =>
      request<AssetOut[]>(`/assets/awaiting-install${query(params)}`),
    /** "What is this thing" — scanned off hardware already on a wall. */
    bySerial: (serial: string) => request<AssetOut>(`/assets/by-serial/${encodeURIComponent(serial)}`),
    commission: (id: string, body: CommissionRequest) =>
      request<CommissionResult>(`/assets/${id}/commission`, { method: "POST", body }),
  },

  /** The reports (INSIGHT-1). */
  reports: {
    overview: (params: { days?: number; months?: number } = {}) =>
      request<OverviewOut>(`/reports/overview${query(params as Record<string, string | number>)}`),
  },

  /** Test sheets, what they certify and what is due (COMPLY-1). */
  forms: {
    templates: () => request<FormTemplateOut[]>("/forms/templates"),
    addTemplate: (body: Partial<FormTemplateOut>) =>
      request<FormTemplateOut>("/forms/templates", { method: "POST", body }),
    updateTemplate: (id: string, body: Partial<FormTemplateOut>) =>
      request<FormTemplateOut>(`/forms/templates/${id}`, { method: "PATCH", body }),
    due: (params: { within_days?: number; site_id?: string } = {}) =>
      request<TestDueRow[]>(`/forms/due${query(params as Record<string, string | number>)}`),
    submit: (body: SubmissionIn) => request<FormSubmissionOut>("/forms/submissions", { method: "POST", body }),
    submissions: (params: { asset_id?: string; job_id?: string; site_id?: string } = {}) =>
      request<FormSubmissionOut[]>(`/forms/submissions${query(params as Record<string, string | number>)}`),
  },

  certificates: {
    list: (params: { site_id?: string; q?: string } = {}) =>
      request<CertificateOut[]>(`/certificates${query(params as Record<string, string | number>)}`),
  },

  catalogue: {
    kits: () => request<KitOut[]>("/catalogue/kits"),
    items: (q?: string, opts: { kind?: string; include_retired?: boolean } = {}) =>
      request<CatalogueItemOut[]>(
        `/catalogue/items${query({ q, kind: opts.kind, include_retired: opts.include_retired ? "true" : undefined })}`,
      ),
    addItem: (body: ItemIn) => request<CatalogueItemOut>("/catalogue/items", { method: "POST", body }),
    updateItem: (id: string, body: Partial<ItemIn>) =>
      request<CatalogueItemOut>(`/catalogue/items/${id}`, { method: "PATCH", body }),
    retireItem: (id: string) => request<CatalogueItemOut>(`/catalogue/items/${id}/retire`, { method: "POST" }),
    suppliers: () => request<SupplierOut[]>("/catalogue/suppliers"),
    addSupplier: (body: { name: string; account_code?: string }) =>
      request<SupplierOut>("/catalogue/suppliers", { method: "POST", body }),
    kit: (id: string) => request<KitDetailOut>(`/catalogue/kits/${id}`),
    addKit: (body: KitIn) => request<KitDetailOut>("/catalogue/kits", { method: "POST", body }),
    updateKit: (id: string, body: Partial<KitIn>) =>
      request<KitDetailOut>(`/catalogue/kits/${id}`, { method: "PATCH", body }),
    removeKit: (id: string) => request<{ retired: string }>(`/catalogue/kits/${id}`, { method: "DELETE" }),
    addKitLine: (
      kitId: string,
      body: { catalogue_item_id: string; qty_formula?: string; is_optional?: boolean; include_by_default?: boolean; note?: string },
    ) => request<KitDetailOut>(`/catalogue/kits/${kitId}/lines`, { method: "POST", body }),
    updateKitLine: (
      kitId: string,
      lineId: string,
      body: { qty_formula?: string; is_optional?: boolean; include_by_default?: boolean; note?: string | null },
    ) => request<KitDetailOut>(`/catalogue/kits/${kitId}/lines/${lineId}`, { method: "PATCH", body }),
    removeKitLine: (kitId: string, lineId: string) =>
      request<KitDetailOut>(`/catalogue/kits/${kitId}/lines/${lineId}`, { method: "DELETE" }),
    previewKit: (kitId: string, driverQty: number, targetMarginBp: number) =>
      request<KitPreviewOut>(
        `/catalogue/kits/${kitId}/preview${query({
          driver_qty: driverQty,
          target_margin_bp: targetMarginBp,
        })}`,
      ),
  },

  /**
   * The offline half. These four are the only calls the sync engine makes —
   * everything else above is a convenience for screens that happen to be
   * online. `src/lib/sync.ts` wraps them in a Transport so it can be tested
   * without a network.
   */
  sync: {
    manifest: () =>
      request<{
        entities: string[];
        horizon_hours: number;
        lookback_hours: number;
        events_per_asset: number;
        page_size: number;
      }>("/sync/manifest"),
    workingSet: (params: { horizon_hours?: number; include_unassigned?: boolean } = {}) =>
      request<WorkingSetResponse>(`/sync/working-set${query(params as Record<string, string | number>)}`),
    push: (body: { device_id: string; changes: unknown[] }) =>
      request<PushResponse>("/sync/push", { method: "POST", body }),
    // The device's own id, so the server leaves out what this phone itself
    // pushed. The cursor used to be moved up to the push response instead, and
    // that skipped everything other people had changed since the last pull,
    // and everything the server worked out from this very push (D81).
    pull: (since: number) => request<PullResponse>(`/sync/pull${query({ since, device_id: device().id })}`),
  },

  health: () => request<{ status: string; env: string; version?: string }>("/health"),
};

/** Built here rather than in sync.ts so the transport has no import cycle. */
export const transport = {
  push: api.sync.push,
  pull: api.sync.pull,
  workingSet: () => api.sync.workingSet(),
};

function query(params: Record<string, string | number | undefined>): string {
  const pairs = Object.entries(params).filter(([, v]) => v !== undefined && v !== "");
  if (pairs.length === 0) return "";
  return `?${new URLSearchParams(pairs.map(([k, v]) => [k, String(v)])).toString()}`;
}
